import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { SharedItemClient } from "@/Common/Api/SharedItem/SharedItemClient";
import { PortalCommunicationsThreadsViewComponent } from "@/Common/Components/SharedItem/PortalCommunicationsThreadsViewComponent";
import { PortalCommunicationsView } from "@/Common/Components/SharedItem/PortalCommunicationsView";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemActionType } from "@/Common/Models/Portal/SharedItem/SharedItemActionType";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { PortalCommunicationComposite } from "@/Common/Models/PortalCommunication/PortalCommunicationComposite";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PortalCommunicationCreateMessage } from "@/Communications/Components/PortalCommunication/PortalCommunicationCreateMessage";
import { FilterSearchComponent } from "./FilterSearchComponent";

import styles from "./PortalCommunicationHistoryView.module.scss";

interface IPortalCommunicationHistoryViewProps {
    patient: Patient;
}

interface IPortalCommunicationsFilterSearch {
    searchText: string;
    dateFrom: Date | null;
    dateTo: Date | null;
}

function PortalCommunicationHistoryView(props: Readonly<IPortalCommunicationHistoryViewProps>): JSX.Element {
    const mainContainer = useRef<HTMLDivElement>(null);
    const [threads, setThreads] = useState<PortalCommunicationComposite[]>();
    const [currentSharedItemId, setCurrentSharedItemId] = useState<string>(patientStore.patientHistoryPortalCommunicationsCurrentThread[props.patient?.IndividualResponse.Contact_id] ?? null);
    const [actionType, setActionType] = useState<SharedItemActionType>(SharedItemActionType.View);
    const [portalCommunicationsFilterSearch, setPortalCommunicationsFilterSearch] = useState<IPortalCommunicationsFilterSearch>({ searchText: "", dateFrom: null, dateTo: null });
    const updateCurrentSharedItemId = (sharedItemId: string): void => {
        if (currentSharedItemId !== sharedItemId) {
            setCurrentSharedItemId(sharedItemId);
            sessionStore.dispatch(LocalPatientOperations.History.PortalCommunicationsSetCurrentThread, { sharedItemId: sharedItemId });
        }
    };

    const toggleFrontedReadStatus = (sharedItemId: string, isMarkAsRead: boolean = false): void => {
        // To update the read status of the thread in the UI, we need to set the DateReadByUser to null if it is unread and set it to current date if it is read.
        // This changes are already set in the backend but since we aren't fetching the list again, we need to update the UI manually.
        if (!threads)
            return;
        const readDate = isMarkAsRead ? new Date() : null;
        setThreads(prevThreads =>
            prevThreads.map(thread =>
                thread.sharedItem.SharedItemId === sharedItemId
                    ? new PortalCommunicationComposite({
                        ...thread,
                        sharedItem: {
                            ...thread.sharedItem,
                            DateReadByUser: readDate
                        }
                    })
                    : thread
            ));

        if (!isMarkAsRead)
            updateCurrentSharedItemId(null);
    };

    const handleThreadClick = (sharedItemId: string): void => {
        updateCurrentSharedItemId(sharedItemId);
        setActionType(SharedItemActionType.View);
        toggleFrontedReadStatus(sharedItemId, true);
    };

    const handleMessageSent = (sharedItemId: string, messages: SharedItemMessage[]): void => {
        setThreads(threads => {
            const currentThread = threads.find(
                thread => thread.sharedItem.SharedItemId === sharedItemId
            );

            if (currentThread) {
                const latestMessage = messages.sortByPropertyDesc(p => p.DateCreated)[0];
                currentThread.latestEmployeeSharedItemMessage = latestMessage;
                currentThread.numberOfSharedItemMessages += 1;
            }

            return [...threads];
        });

        // refresh the filter. The new message may be included in the filter
        handleFilterChange({});

        if (actionType === SharedItemActionType.New) {
            handleThreadClick(sharedItemId);
        }
    };

    const filteredThreads = useMemo(() => {
        if (!threads)
            return [];

        const { searchText, dateFrom, dateTo } = portalCommunicationsFilterSearch;

        if (IsNullOrWhiteSpace(searchText) && dateFrom === null && dateTo === null)
            return threads;

        return threads.filter(thread => {
            const lastMessage = thread.getLatestSharedItemMessage();

            const matchesSearchText = searchText
                ? `${thread.sharedItem.Subject} ${lastMessage?.Text}`.toLowerCase().includes(searchText.toLowerCase())
                : true;

            const itemDate = LuxonDateTimeParser.parse(lastMessage?.DateCreated ?? thread.sharedItem.DateCreated);

            const isWithinDateRange =
            (!dateFrom || itemDate >= LuxonDateTimeParser.parse(dateFrom).startOf("day")) &&
            (!dateTo || itemDate <= LuxonDateTimeParser.parse(dateTo).endOf("day"));

            return matchesSearchText && isWithinDateRange;
        });
    }, [threads, portalCommunicationsFilterSearch]);

    const handleFilterChange = (newFilter: Partial<typeof portalCommunicationsFilterSearch>): void => {
        setPortalCommunicationsFilterSearch(prev => ({ ...prev, ...newFilter }));
    };

    const getThreadsAsync = useCallback(async (): Promise<void> => {
        const patientContactId = props.patient?.IndividualResponse.Contact_id;
        if (patientContactId) {
            const response = await SharedItemClient.fetchPatientPortalCommunications({
                PatientContactId: patientContactId
            });

            setThreads(response.portalCommunicationComposites);
        }
        else {
            setThreads([]);
        }
    }, [props.patient?.IndividualResponse.Contact_id]);

    const updateSharedItemsStatus = (sharedItemId: string, status: PortalSyncStatus, includeLinkedItem: boolean, date: Date): void => {
        setThreads?.(threads =>
            threads.map(thread => {
                if (thread.sharedItem.SharedItemId === sharedItemId) {
                    return new PortalCommunicationComposite({
                        ...thread,
                        sharedItem: new SharedItem({
                            ...thread.sharedItem,
                            Status: status,
                            IncludeLinkedItem: includeLinkedItem,
                            LastStatusDate: date
                        }),
                        latestEmployeeSharedItemMessage: new SharedItemMessage({
                            ...thread.latestEmployeeSharedItemMessage,
                            Status: status,
                            LastStatusDate: date
                        })
                    });
                }
                return thread;
            }));
    };

    useEffect(() => {
        getThreadsAsync();
    }, [props.patient, portalCommunicationsFilterSearch, getThreadsAsync]);

    useEffect(() => {
        const onResize = (): void => {
            const container = mainContainer.current;
            if (!container) return;

            const yPosition = container.getBoundingClientRect().y;
            mainContainer.current.style.setProperty("--portal-communications-top", `${yPosition}px`);
        };

        onResize();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    if (!props.patient)
        return null;

    return (
        <div className={styles.communicationsHistoryMainContainer} ref={mainContainer}>
            <div className={styles.portalCommunicationHistoryContainer}>
                <div className={styles.leftContainer} data-cy="portal-communication-filter-search-container">
                    <FilterSearchComponent
                        searchText={portalCommunicationsFilterSearch.searchText}
                        dateFrom={portalCommunicationsFilterSearch.dateFrom}
                        dateTo={portalCommunicationsFilterSearch.dateTo}
                        onSearchTextChange={searchText => handleFilterChange({ searchText })}
                        onDateFromChange={dateFrom => handleFilterChange({ dateFrom })}
                        onDateToChange={dateTo => handleFilterChange({ dateTo })}
                        onCreateNewItem={() => {
                            updateCurrentSharedItemId(null);
                            setActionType(SharedItemActionType.New);
                        }}
                    />
                    <div className={styles.threadsList}>
                        <PortalCommunicationsThreadsViewComponent
                            patient={props.patient}
                            selectedSharedItemId={currentSharedItemId}
                            threads={filteredThreads}
                            onClick={sharedItemId => handleThreadClick(sharedItemId)}
                        />
                    </div>
                </div>
                {currentSharedItemId && actionType === SharedItemActionType.View && (
                    <PortalCommunicationsView
                        patient={props.patient}
                        currentSharedItemId={currentSharedItemId}
                        onToggleHistoryReadStatus={toggleFrontedReadStatus}
                        onMessageSent={handleMessageSent}
                        onArchivedRestore={() => updateCurrentSharedItemId(null)}
                        onSyncOrRevokeSharedItemsAttachment={updateSharedItemsStatus}
                        onRevokeAll={(currentSharedItemId, status, includeLinkedItem, date) => {
                            updateSharedItemsStatus(currentSharedItemId, status, includeLinkedItem, date);
                            updateCurrentSharedItemId(null);
                        }}
                    />
                )}
                {actionType === SharedItemActionType.New && (
                    <PortalCommunicationCreateMessage
                        patient={Person.FromPatient(props.patient)}
                        onMessageSent={handleMessageSent}
                        onClose={() => {
                            updateCurrentSharedItemId(null);
                            setActionType(SharedItemActionType.View);
                        }}
                    />
                )}
            </div>
        </div>
    );
}

export { PortalCommunicationHistoryView };
